import React, { useMemo, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

const POINTS_ERROR = "Points must be between 1 to 10000";

function readError(error) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "Failed to fetch suggestions"
  );
}

export default function MatkaGroupJodi({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0]
  );
  const [points, setPoints] = useState("");
  const [jodiValue, setJodiValue] = useState("");
  const [pointsError, setPointsError] = useState("");
  const [jodiError, setJodiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState([]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );

  const handlePointsChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 5);
    const next = digits === "" ? "" : String(parseInt(digits, 10));
    setPoints(next);
    if (!next) {
      setPointsError("");
      return;
    }
    const value = parseInt(next, 10);
    setPointsError(value >= 1 && value <= 10000 ? "" : POINTS_ERROR);
  };

  const handleJodiChange = (event) => {
    setJodiValue(event.target.value.replace(/\D/g, "").slice(0, 2));
    setJodiError("");
  };

  const handleAddBids = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (loading) return;

    const pointsValue = parseInt(points, 10);
    if (!points.trim() || !Number.isInteger(pointsValue) || pointsValue < 1 || pointsValue > 10000) {
      setPointsError(POINTS_ERROR);
      return;
    }
    if (jodiValue.length !== 2) {
      setJodiError("Please enter a 2-digit number");
      return;
    }

    setLoading(true);
    setJodiError("");
    try {
      const data = await fetchMatkaSuggestions("group_jodi", jodiValue);
      const jodis = Array.isArray(data?.jodis)
        ? data.jodis.map(String).filter((jodi) => /^\d{2}$/.test(jodi))
        : [];
      if (!jodis.length) {
        setJodiError("Failed to fetch suggestions");
        return;
      }

      setBids((currentBids) => {
        const nextBids = [...currentBids];
        jodis.forEach((jodi) => {
          const existingIndex = nextBids.findIndex(
            (bid) => bid.jodi === jodi && bid.gameType === gameType
          );
          if (existingIndex >= 0) {
            nextBids[existingIndex] = {
              ...nextBids[existingIndex],
              points: Number(nextBids[existingIndex].points) + pointsValue,
            };
            return;
          }
          nextBids.push({
            id: `${jodi}-${gameType}-${Date.now()}-${nextBids.length}`,
            jodi,
            points: pointsValue,
            gameType,
          });
        });
        return nextBids;
      });
      setJodiValue("");
      setPoints("");
      setPointsError("");
    } catch (error) {
      setJodiError(readError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextGameType, (bid) => bid.jodi)
    );
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Title */}
          <h1 className="matka-jodi-title mb-4">GROUP JODI</h1>
          <div className="matka-game-type-selector-wrapper mb-4">
            <label className="matka-game-type-label">GAME TYPE</label>
            <MatkaSessionSelect
              betDirection={selectedGameType?.betDirection}
              value={gameType}
              onChange={handleGameTypeChange}
            />
          </div>

          {/* Form Inputs (ENTER POINTS, then ENTER JODI) */}
          <div className="matka-jodi-form mb-4">
            <div className="matka-jodi-input-group mb-4">
              <label className="matka-jodi-label">ENTER POINTS</label>
              <input
                type="text"
                inputMode="numeric"
                className="matka-jodi-input"
                placeholder="Enter Points (e.g. 100)"
                value={points}
                onChange={handlePointsChange}
              />
              {pointsError && <p className="matka-sangam-error">{pointsError}</p>}
            </div>

            <div className="matka-jodi-input-group mb-4">
              <label className="matka-jodi-label">ENTER JODI</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={2}
                className="matka-jodi-input"
                placeholder="Enter Jodi (e.g. 56)"
                value={jodiValue}
                onChange={handleJodiChange}
              />
              {loading && (
                <p className="matka-pana-suggestion-status">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </p>
              )}
              {jodiError && <p className="matka-sangam-error">{jodiError}</p>}
            </div>

            <div className="d-flex justify-content-end mb-4">
              <button
                type="button"
                className="matka-add-bid-btn"
                onClick={handleAddBids}
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-label="Loading"
                  ></span>
                ) : (
                  "ADD BIDS"
                )}
              </button>
            </div>
          </div>

          {/* Subtle Divider */}
          <div className="matka-jodi-divider mb-4" />

          {/* Bids Table Container */}
          <div className="matka-jodi-table-container mb-4">
            <table className="matka-jodi-table">
              <thead>
                <tr>
                  <th className="th-jodi">JODI</th>
                  <th className="th-points text-center">POINTS</th>
                  <th className="th-gametype text-end">GAME TYPE</th>
                  <th className="th-action" aria-label="Delete"></th>
                </tr>
              </thead>
              <tbody>
                {bids.map((bid) => (
                  <tr key={bid.id}>
                    <td className="td-jodi">{bid.jodi}</td>
                    <td className="td-points text-center">{bid.points}</td>
                    <td className="td-gametype text-end">{bid.gameType}</td>
                    <td className="td-action text-end">
                      <button
                        type="button"
                        className="matka-jodi-delete-btn"
                        onClick={() => handleDeleteBid(bid.id)}
                        aria-label={`Delete ${bid.jodi}`}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
